import React, { Component } from 'react'
import { connect } from 'react-redux'

import setActivePackage from '../../../../actions/setActivePackage'
import removeActivePackage from '../../../../actions/removeActivePackage'
import setActiveStep from '../../../../actions/setActiveStepActions'
import Checkbox from '../../../../components/elements/Checkbox'
import updateSearchParams from '../../../../utilities/updateSearchParams'
import shouldDisableRowButtons from '../shouldDisableRowButtons'
import { getPackagesDetails } from '../../../../reducers/fetchPackagesReducers'
import { getActivePackageKeys } from '../../../../reducers/setActivePackageReducers'
import triggerCustomGaEvent from '../../../../utilities/triggerCustomGaEvent'
import {
  clearPackageCode,
  clearGroupId
} from '../../../../actions/setActiveSearchFilters'
import { fetchGroupAvailability } from '../../../../actions/fetchGroupAvailability'
import { setGroupAvailabilityData } from '../../../../actions/setGroupAvailabilityData'
import { isCategoryGroupAvailable } from '../../../../utilities/getGroupFilteredPricing'

const hasAvailableGroupCabins = (categoryOptions, packageDetails) => {
  // Missing categoryOptions = no group data (e.g. fetch failed) — don't filter.
  // An empty array means the group has no bookable allocations and falls
  // through to isCategoryGroupAvailable, which will return false for every
  // cabin.
  if (!categoryOptions) return true

  const tabularPricing = packageDetails?.tabularPricing
  if (!tabularPricing) return true

  return Object.values(tabularPricing).some(superCat => {
    if (!superCat?.pricing) return false
    return Object.values(superCat.pricing).some(cabin => {
      if (!cabin?.category) return false
      return isCategoryGroupAvailable(categoryOptions, cabin.category)
    })
  })
}

class EQuoteSailingsSelectPackageButton extends Component {
  state = {
    isCheckingGroupAvailability: false,
    groupUnavailableMessage: null
  }

  componentDidMount() {
    this.checkForPreSelectedPackage()
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    this.checkForPreSelectedPackage()
  }

  checkForPreSelectedPackage() {
    const {
      activeStep,
      activeSearchFilterData,
      isDisabled,
      handleNextStep,
      isSelected,
      packageDetails
    } = this.props

    const hasGroupIdFilter = Boolean(activeSearchFilterData.groupId)
    const packageCodeMatches =
      activeSearchFilterData.packageCode &&
      activeSearchFilterData.packageCode === packageDetails.id
    const startDateMatches =
      activeSearchFilterData.startDate &&
      activeSearchFilterData.startDate === packageDetails.sailDate

    // When GroupID is present in the URL, prefer the matching group sailing
    // row over the individual sailing. PackageCode + GroupID uniquely identify
    // the row, so the startDate match is not required in that case (the URL
    // may not include DepartureDate). Otherwise, fall back to selecting the
    // individual sailing using the existing PackageCode + startDate match.
    const isPreSelectedRow = hasGroupIdFilter
      ? packageDetails.pricingType === 'Group' &&
        String(packageDetails.groupId) ===
          String(activeSearchFilterData.groupId) &&
        packageCodeMatches
      : packageDetails.pricingType === 'Individual' &&
        packageCodeMatches &&
        startDateMatches

    if (
      !isSelected &&
      !this.preSelectionInFlight &&
      isPreSelectedRow &&
      !isDisabled
    ) {
      this.preSelectionInFlight = true
      const filtersAtClick = activeSearchFilterData
      this.addPackageWithAvailabilityCheck().then(added => {
        this.preSelectionInFlight = false
        // Clear in either case so a failed check doesn't retrigger on every update.
        this.props.clearPackageCode()
        this.props.clearGroupId()
        if (added) {
          updateSearchParams(filtersAtClick)
          this.props.handleNextStep(activeStep)
        }
      })
    }
  }

  addPackageWithAvailabilityCheck = async () => {
    const { packageDetails, agencyId, identifier } = this.props

    if (!packageDetails.groupId) {
      this.props.handleAddPackage()
      return true
    }

    this.setState({
      isCheckingGroupAvailability: true,
      groupUnavailableMessage: null
    })

    const groupData = await fetchGroupAvailability({
      groupId: packageDetails.groupId,
      agencyId
    })

    if (!hasAvailableGroupCabins(groupData?.categoryOptions, packageDetails)) {
      this.setState({
        isCheckingGroupAvailability: false,
        groupUnavailableMessage: 'No group cabins available'
      })
      return false
    }

    // Cache so downstream price rollups (selected-package modal, etc.) can filter
    // out cabin categories that are sold out for this group before computing the
    // displayed "from" price.
    this.props.cacheGroupAvailability(identifier, groupData)
    this.setState({ isCheckingGroupAvailability: false })
    this.props.handleAddPackage()
    return true
  }

  handleCheckboxClick = () => {
    if (this.props.isSelected) {
      this.props.handleRemovePackage()
      return
    }
    this.addPackageWithAvailabilityCheck()
  }

  render() {
    const { packageDetails, isDisabled, isSelected, shouldRender } = this.props
    const { isCheckingGroupAvailability, groupUnavailableMessage } = this.state
    if (!shouldRender) return null

    return (
      <>
        <Checkbox
          key={packageDetails.id}
          checked={isSelected}
          onClick={this.handleCheckboxClick}
          disabled={isDisabled}
          isLoading={isCheckingGroupAvailability}
        />
        {groupUnavailableMessage && (
          <div className="equote__sailings-table-group-unavailable">
            {groupUnavailableMessage}
          </div>
        )}
      </>
    )
  }
}

function mapStateToProps(state, ownProps) {
  const {
    identifier,
    isLandPackage,
    cruiseIdentifier,
    isCruiseTour,
    packageIdentifier: parentPackageIdentifier
  } = ownProps
  const activePackageKeys = getActivePackageKeys(state)
  const activePackageCount = activePackageKeys.length

  const packageIdentifier = isLandPackage ? cruiseIdentifier : identifier
  const packageDetails = getPackagesDetails(
    state,
    isCruiseTour,
    packageIdentifier
  )

  const isSelected = activePackageKeys.some(key => key === identifier)
  const parentPackageIsSelected = activePackageKeys
    .map(key => getPackagesDetails(state, isCruiseTour, key))
    .some(
      ({ packageIdentifier }) => packageIdentifier === parentPackageIdentifier
    )

  // For group sailings, fetchPackages preloads categoryOptions onto the
  // package. If none of the package's cabin categories are bookable for the
  // group, hide the checkbox upfront rather than relying on the lazy
  // post-click check.
  const isGroupSailing = Boolean(packageDetails.groupId)
  const hasGroupAvailability =
    !isGroupSailing ||
    hasAvailableGroupCabins(packageDetails.categoryOptions, packageDetails)

  return {
    activeStep: state.activeStepData.activeStep,
    shouldRender:
      !shouldDisableRowButtons(packageDetails) && hasGroupAvailability,
    activeSearchFilterData: state.activeSearchFilterData,
    identifier,
    isSelected,
    activePackageCount,
    packageDetails,
    isDisabled:
      !isSelected && (activePackageCount === 5 || parentPackageIsSelected),
    agencyId:
      state.ssoData?.transactionData?.agency?.agencyId ??
      state.ssoData?.agency?.agency_id
  }
}

const mapDispatchToProps = (dispatch, ownProps) => {
  const {
    identifier,
    isLandPackage,
    cruiseIdentifier,
    landPackageId,
    stateKey,
    landProgramType
  } = ownProps
  return {
    handleAddPackage() {
      if (isLandPackage) {
        triggerCustomGaEvent('isLandPackage', { ...ownProps })
      }
      dispatch(
        setActivePackage(identifier, {
          stateKey,
          cruiseIdentifier: isLandPackage ? cruiseIdentifier : identifier,
          isLandPackage,
          landPackageId: isLandPackage ? landPackageId : null,
          landPackageType:
            isLandPackage && landProgramType ? landProgramType : null
        })
      )
    },
    handleRemovePackage() {
      return dispatch(removeActivePackage(identifier))
    },
    handleNextStep(activeStep) {
      return dispatch(setActiveStep(activeStep + 1))
    },
    clearPackageCode() {
      dispatch(clearPackageCode())
    },
    clearGroupId() {
      dispatch(clearGroupId())
    },
    cacheGroupAvailability(key, data) {
      dispatch(setGroupAvailabilityData(key, data))
    }
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(EQuoteSailingsSelectPackageButton)
